/**
 * The web's type scale — the same fourteen rungs as the phone
 * (specification/design-system.md §2.6, mobile `signature_tokens.dart`), and
 * the on-the-fly switch between them.
 *
 * On the web a switch is ONE attribute on <html>: every rung is a CSS variable
 * in index.css that reads `[data-type-scale]`, and Ant Design gets the matching
 * numbers through its theme tokens (App.tsx). Persisted per browser.
 *
 * `proposed` is the scale the design system documents. `comfortable` is every
 * rung one step up — body 15 instead of 13.5, because Apple's guideline for
 * body text is 17 and Google's 14–16; eyebrows stay 10. The PO compared both
 * on real pages before choosing (design/type-scale-*-mockup.html).
 */
export type TypeScale = 'proposed' | 'comfortable';

export const TYPE_SCALE_STORAGE_KEY = 'swiftops.typeScale';

export const TYPE_SCALES: readonly TypeScale[] = ['proposed', 'comfortable'];

/** Size per rung, in px. Weights and colours do not change with the scale. */
export const RUNGS = {
  proposed: {
    display: 24, h1: 21, h2: 18, stat: 22, subtitle: 16, title: 14.5,
    strong: 13.5, body: 13.5, hint: 13, meta: 12, label: 12, caption: 11.5,
    pill: 11.5, micro: 10,
  },
  comfortable: {
    display: 26, h1: 22, h2: 20, stat: 24, subtitle: 17, title: 16,
    strong: 15, body: 15, hint: 14, meta: 13, label: 12, caption: 12,
    pill: 12, micro: 10,
  },
} as const;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;
type RootLike = { dataset: DOMStringMap | Record<string, string | undefined> };

function isTypeScale(v: unknown): v is TypeScale {
  return v === 'proposed' || v === 'comfortable';
}

/** The remembered choice, or `proposed`. Never throws — a locked-down browser
 *  that refuses storage just gets the default. */
export function readTypeScale(storage?: StorageLike): TypeScale {
  try {
    const raw = (storage ?? localStorage).getItem(TYPE_SCALE_STORAGE_KEY);
    return isTypeScale(raw) ? raw : 'proposed';
  } catch {
    return 'proposed';
  }
}

/** Apply a scale: the attribute every rung reads, and the memory of it. */
export function applyTypeScale(
  scale: TypeScale,
  root: RootLike = document.documentElement,
  storage?: StorageLike,
): void {
  root.dataset.typeScale = scale;
  try {
    (storage ?? localStorage).setItem(TYPE_SCALE_STORAGE_KEY, scale);
  } catch {
    /* storage refused — the choice still applies for this page */
  }
}

/** Ant Design's size tokens for a scale, so its components sit on the same
 *  rungs as everything else: base = body, SM = meta, LG = title, XL = subtitle,
 *  headings h1…h5 down the top of the scale.
 *
 *  The HEADING line heights are set too: AntD derives `lineHeightHeading*`
 *  from ITS OWN size ladder before the overrides are merged, so without these
 *  a level-2 title (22px) sat on a shorter box than a level-3 one (21px) —
 *  audit, 2026-09-11. The values are the rungs' own.
 *
 *  The body / small / large line heights are deliberately NOT set: AntD's own
 *  (1.59 / 1.67 / 1.5 at base 13.5) are what every page was shot with, and
 *  pinning them to the CSS rungs once (1.5 / 1.35 / 1.3) moved every text line
 *  on 81 of 82 pages by 1–4px — the camera caught it the same day. Tightening
 *  the whole app's line height is a design decision for the PO, not a pin. */
export function antdTypeTokens(scale: TypeScale) {
  const r = RUNGS[scale];
  return {
    fontSize: r.body,
    fontSizeSM: r.meta,
    fontSizeLG: r.title,
    fontSizeXL: r.subtitle,
    fontSizeHeading1: r.display,
    fontSizeHeading2: r.stat,
    fontSizeHeading3: r.h1,
    fontSizeHeading4: r.h2,
    fontSizeHeading5: r.subtitle,
    // Ordered so a bigger heading always sits on a bigger box: 24×1.15 > 22×1.2 > 21×1.2 > 18×1.25 > 16×1.3
    lineHeightHeading1: 1.15,
    lineHeightHeading2: 1.2,
    lineHeightHeading3: 1.2,
    lineHeightHeading4: 1.25,
    lineHeightHeading5: 1.3,
  };
}

/** Component tokens that AntD would otherwise DERIVE from the sizes above in
 *  ways nobody chose: a radio's circle from fontSizeLG (14.5 — smaller than the
 *  16px checkbox beside it, and the only control that resized with the switch),
 *  and a Statistic's number from the heading ladder (21) instead of the `stat`
 *  rung. Audit, 2026-09-11. */
export function antdTypeComponents(scale: TypeScale) {
  const r = RUNGS[scale];
  return {
    Radio: { radioSize: 16, dotSize: 6 },   // AntD's own defaults at base 14: 16 and 16 − (4 + 1) × 2
    Statistic: { contentFontSize: r.stat, titleFontSize: r.meta },
  };
}
